import {
  API_BASE_URL,
  chatInput,
  sendBtn,
  chatBody,
  apiStatus,
  navOptions,
  AppState // <-- Import AppState
} from './globals.js';
import { loadDeals } from './data.js';

// ============================================
// API FUNCTIONS
// ============================================

export async function checkAPIStatus() {
  try {
    const sessionIdToSend = AppState.SESSION_ID || "guest_session";

    const response = await fetch(`${API_BASE_URL}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: "test",
        session_id: sessionIdToSend,
        user_id: AppState.user_uid,
        country: AppState.selectedCountryName,
      }),
    });
    if (response.ok) {
      apiStatus.innerHTML =
        '<i class="fas fa-circle" style="color: #4caf50; font-size: 0.7rem"></i> Connected';
    } else {
      apiStatus.innerHTML =
        '<i class="fas fa-circle" style="color: #ff9800; font-size: 0.7rem"></i> API Error';
    }
  } catch (error) {
    apiStatus.innerHTML =
      '<i class="fas fa-circle" style="color: #f44336; font-size: 0.7rem"></i> Offline';
    console.error("API connection error:", error);
  }
}

export async function sendMessageToAPI(message) {
  try {
    const sessionIdToSend = AppState.SESSION_ID || "guest_session";
    const effectiveUserId = AppState.isLoggedIn ? AppState.user_uid : sessionIdToSend;
    const response = await fetch(`${API_BASE_URL}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: message,
        conversation: formatConversationForAPI(),
        session_id: sessionIdToSend,
        user_id: effectiveUserId,
        country: AppState.selectedCountryName,
      }),
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.response;
  } catch (error) {
    console.error("Error sending message:", error);
    return "Sorry, I'm having trouble connecting to the server. Please try again later.";
  }
}

// ============================================
// CHAT FUNCTIONS
// ============================================

export function getCurrentTime() {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
}

export function shouldSwitchToDealsView(content) {
  const dealPatterns = [
    "📢 Deal Info:",
    "🔎 Click to see the complete list on the Deal page",
    "Here are the best deals",
    "I found these deals",
    "Here are some deals for",
    "Current deals for",
  ];

  return dealPatterns.some((pattern) => content.includes(pattern));
}

export function switchToDealsView() {
  navOptions.forEach((opt) => {
    if (opt.textContent.includes("Deals")) {
      opt.style.background = "rgba(220, 20, 60, 0.15)";
      opt.style.color = "#DC143C";
      opt.style.boxShadow = "0 8px 15px rgba(220, 20, 60, 0.15)";
    } else {
      opt.style.background = "rgba(255, 255, 255, 0.7)";
      opt.style.color = "#444";
      opt.style.boxShadow = "none";
    }
  });
  loadDeals(true);
}

export function formatResponse(content) {
  if (!content) return content;
  if (shouldSwitchToDealsView(content)) {
    setTimeout(() => {
      switchToDealsView();
    }, 100);
  }

  const lines = content.split("\n");
  let formattedContent = "";

  for (const line of lines) {
    const trimmedLine = line.trim();
    let processedLine = trimmedLine;

    // Remove bullet points (•) from AI responses
    if (processedLine.startsWith("•")) {
      processedLine = processedLine.substring(1).trim();
    }

    processedLine = processedLine.replace(
      /(Valid till\s+.+?)(?=\.|,|\n|$)/gi,
      '<span class="valid-till">$1</span>',
    );

    if (trimmedLine.startsWith("•")) {
      formattedContent += `<div class="bullet-point">${processedLine}</div>`;
    } else if (trimmedLine.includes("📢")) {
      formattedContent += `<div class="deal-alert">${processedLine}</div>`;
    } else if (trimmedLine.includes("🔎")) {
      formattedContent += `<div class="deal-footer">${processedLine}</div>`;
    } else if (
      trimmedLine.includes("Want any other product") ||
      trimmedLine.includes("Great!") ||
      trimmedLine.includes("Looking for")
    ) {
      formattedContent += `<div class="suggestion">${processedLine}</div>`;
    } else if (trimmedLine.startsWith("•")) {
      formattedContent += `<div class="regular-text">${processedLine}</div>`;
    } else if (trimmedLine) {
      formattedContent += `<div class="regular-text">${processedLine}</div>`;
    }
  }
  return formattedContent;
}

export function addMessage(content, isSent = false, time = null) {
  const messageDiv = document.createElement("div");
  messageDiv.className = `message ${isSent ? "sent" : "received"}`;
  const timeString = time || getCurrentTime();
  let messageContent = content;

  if (!isSent) {
    messageContent = formatResponse(content);
  }

  messageDiv.innerHTML = `
    <div class="message-content">${isSent ? content : messageContent}</div>
    <div class="message-time">${timeString}</div>
  `;

  if (!isSent && content.includes("📢 Deal Info")) {
    const contentContainer = messageDiv.querySelector(".message-content");
    contentContainer.classList.add("clickable-deal-message");

    contentContainer.addEventListener("click", () => {
      switchToDealsView();

      if (window.innerWidth <= 992) {
        setSidebarState(false);
      }
    });
  }

  chatBody.appendChild(messageDiv);
  chatBody.scrollTop = chatBody.scrollHeight;
}

export function showLoading() {
  const loadingDiv = document.createElement("div");
  loadingDiv.className = "message received";
  loadingDiv.id = "loadingMessage";
  loadingDiv.innerHTML = `
    <div class="message-content">
      <span class="spinner"></span> Just a moment...
    </div>
    <div class="message-time">${getCurrentTime()}</div>
  `;
  chatBody.appendChild(loadingDiv);
  chatBody.scrollTop = chatBody.scrollHeight;
}

export function hideLoading() {
  const loadingElement = document.getElementById("loadingMessage");
  if (loadingElement) loadingElement.remove();
}

export async function sendMessage() {
  const messageText = chatInput.value.trim();
  if (messageText === "") return;

  const currentTime = getCurrentTime();
  addMessage(messageText, true, currentTime);
  addToConversationHistory("user", messageText);

  chatInput.value = "";
  chatInput.focus();
  showLoading();

  try {
    const apiResponse = await sendMessageToAPI(messageText);
    hideLoading();

    const responseTime = getCurrentTime();
    addMessage(apiResponse, false, responseTime);
    addToConversationHistory("AI", apiResponse);

    if (shouldSwitchToDealsView(apiResponse)) {
      setTimeout(() => {
        switchToDealsView();
      }, 200);
    }

    if (apiResponse.includes("Sorry, I'm having trouble connecting")) {
      apiStatus.innerHTML =
        '<i class="fas fa-circle" style="color: #f44336; font-size: 0.7rem"></i> Offline';
    } else {
      apiStatus.innerHTML =
        '<i class="fas fa-circle" style="color: #4caf50; font-size: 0.7rem"></i> Connected';
    }
  } catch (error) {
    hideLoading();
    console.error("Error in sendMessage:", error);
    const errorMessage = "Sorry, an error occurred. Please try again.";
    const errorTime = getCurrentTime();
    addMessage(errorMessage, false, errorTime);
    addToConversationHistory("AI", errorMessage);
  }
}

// ============================================
// CONVERSATION HISTORY MANAGEMENT
// ============================================

export function loadConversationHistory() {
  const savedHistory = localStorage.getItem(
    `walleto_conversation_${AppState.SESSION_ID}`,
  );
  if (savedHistory) {
    try {
      AppState.conversationHistory = JSON.parse(savedHistory);
    } catch (error) {
      console.error("Error loading conversation history:", error);
      AppState.conversationHistory = [];
    }
  } else {
    AppState.conversationHistory = [];
  }
}

export function saveConversationHistory() {
  try {
    localStorage.setItem(
      `walleto_conversation_${AppState.SESSION_ID}`,
      JSON.stringify(AppState.conversationHistory),
    );
  } catch (error) {
    console.error("Error saving conversation history:", error);
  }
}

export function setSidebarState(open, fromPopState = false) {
  const isMobile = window.innerWidth <= 992;

  if (open) {
    mainWrapper.classList.add("sidebar-open");
    chatToggleBtn.innerHTML = '<i class="fas fa-times"></i>';

    if (isMobile && !fromPopState) {
      history.pushState({ chatOpen: true }, "", "#chat");
    }
  } else {
    mainWrapper.classList.remove("sidebar-open");
    chatToggleBtn.innerHTML = '<i class="fa-solid fa-robot"></i>';

    if (isMobile && !fromPopState && window.location.hash === "#chat") {
      history.back();
    }
  }
}

export function addToConversationHistory(role, content) {
  AppState.conversationHistory.push({
    role: role,
    content: content,
    timestamp: new Date().toISOString(),
  });

  if (AppState.conversationHistory.length > 50) {
    AppState.conversationHistory = AppState.conversationHistory.slice(-50);
  }
  saveConversationHistory();
}

export function displayLoadedConversationHistory() {
  chatBody.innerHTML = "";
  if (AppState.conversationHistory.length === 0) {
    const initialMessage = `Hi, I'm Felix 👋 Your smart deal-finder 🛒 Just tell me what you're looking for—milk, chips, pizza, Pepsi, or anything else—and I'll scan flyers from nearby merchants to show you the best available deals. Let's save you some money 💸`;
    addToConversationHistory("AI", initialMessage);
    addMessage(initialMessage, false, getCurrentTime());
  } else {
    AppState.conversationHistory.forEach((msg) => {
      const time = msg.timestamp
        ? formatTimeFromISO(msg.timestamp)
        : getCurrentTime();
      addMessage(msg.content, msg.role === "user", time);
    });
  }
  chatBody.scrollTop = chatBody.scrollHeight;
}

export function formatTimeFromISO(isoString) {
  try {
    const date = new Date(isoString);
    return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  } catch (error) {
    console.error("Error formatting time:", error);
    return getCurrentTime();
  }
}

export function clearConversationHistory() {
  AppState.conversationHistory = [];
  localStorage.removeItem(`walleto_conversation_${AppState.SESSION_ID}`);

  chatBody.innerHTML = "";
  const initialMessage = `Hi, I'm Felix 👋 Your smart deal-finder 🛒 Just tell me what you're looking for—milk, chips, pizza, Pepsi, or anything else—and I'll scan flyers from nearby merchants to show you the best available deals. Let's save you some money 💸`;
  addToConversationHistory("AI", initialMessage);
  addMessage(initialMessage, false, getCurrentTime());
  // console.log("Chat history cleared and reset");
}

export function formatConversationForAPI() {
  return AppState.conversationHistory.map((msg) => {
    if (msg.role === "user") {
      return `{"role":"user","content":"${msg.content.replace(/"/g, '\\"')}"}`;
    } else {
      return `{"role":"AI","content":"${msg.content.replace(/"/g, '\\"')}"}`;
    }
  });
}

// ============================================
// CHAT TOGGLE FUNCTIONALITY (NEW)
// ============================================

const mainWrapper = document.getElementById("mainWrapper");
const chatToggleBtn = document.getElementById("chatToggleBtn");
const rightSidebar = document.getElementById("rightSidebar");

chatToggleBtn.addEventListener("click", function () {
  const isOpen = mainWrapper.classList.contains("sidebar-open");
  setSidebarState(!isOpen);
});

const chatCloseBtn = document.getElementById("chatCloseBtn");
if (chatCloseBtn) {
  chatCloseBtn.addEventListener("click", function () {
    setSidebarState(false);
  });
}

window.addEventListener("popstate", function (event) {
  const isChatOpen = mainWrapper.classList.contains("sidebar-open");
  const isMobile = window.innerWidth <= 992;

  if (isMobile && isChatOpen && window.location.hash !== "#chat") {
    setSidebarState(false, true);
  }
});